import React, { useRef, useState, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const PlayerStats = ({ competitionId, statsFeed, onError }) => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [playerStats, setPlayerStats] = useState([]);
    const [searchQuery, setSearchQuery] = useState(''); // Added search state
    const [currentSlide, setCurrentSlide] = useState(0);
    const [activeOption, setActiveOption] = useState('batting');
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [isEndOfSlides, setIsEndOfSlides] = useState(false);

    const imageURL = process.env.REACT_APP_IMAGE_URL;
    const feedURL = process.env.REACT_APP_FEED_URL;

    const defaultPlayerImage = `${imageURL}player-thumb.jpg`;
    const defaultTeamImage = `${imageURL}default-team-logo.png`;
    const handleImageError = (e) => {
        e.target.src = defaultPlayerImage;
    };
    const handleTeamImageError = (e) => {
        e.target.src = defaultTeamImage;
    };

    const playersPerView = 6; // Adjusted to show more players
    const playerWidth = 100 / playersPerView; // Width percentage for each player
    // Filter players based on search query
    const filteredPlayers = playerStats.filter(player =>
        player.PlayerName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Handle search input change
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentSlide(0); // Reset to first slide when searching
    };
    const sliderRef = useRef(null);

    const sliderSettings = {
        dots: false,
        infinite: false,
        speed: 300,
        slidesToShow: 6,
        slidesToScroll: 6,
        swipe: true,
        arrows: true,
        variableWidth: false,
        adaptiveHeight: false,
        centerMode: false,
        prevArrow: <button className="mcPrevArrow icon-angle-left"></button>,
        nextArrow: <button className="mcNextArrow icon-angle-right"></button>,
        beforeChange: (oldIndex, newIndex) => {
            setCurrentSlide(newIndex / 6);
            // Check if we're at the end of slides
            const totalSlides = filteredPlayers.length;
            const remainingSlides = totalSlides - (newIndex + 6);
            setIsEndOfSlides(remainingSlides <= 0);
        },
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 6,
                    slidesToScroll: 3,
                    variableWidth: true
                }
            },
            {
                breakpoint: 520,
                settings: {
                    slidesToShow:1,
                    slidesToScroll: 1,
                    variableWidth: true
                }
            }
        ],
        className: "player-slider"
    };

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                const timestamp = new Date().getTime();
                setLoading(true);
                const defaultFeed = `${feedURL}`;

                // Fetch the team data
                const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-teamlist.js?onteamlist=_jqjsp&_=${timestamp}`);
                const data = await response.text();

                console.log('Raw team list response:', data); // Debugging log

                // Extract JSON from the JSONP response
                const matches = data.match(/onteamlist\((.*?)\);?$/s);
                if (!matches || !matches[1]) {
                    throw new Error('Invalid team data format');
                }

                // Parse the JSON string
                const jsonStr = matches[1].trim();
                const parsedData = JSON.parse(jsonStr);

                console.log('Parsed team data:', parsedData); // Debugging log

                // Validate the parsed data structure
                if (!parsedData?.Teamlist) {
                    throw new Error('Invalid team data structure');
                }

                // Map the team list to the desired format
                const teamList = parsedData.Teamlist.map(item => ({
                    TeamID: item.TeamID,
                    TeamName: item.TeamName,
                    TeamLogo: item.TeamLogo,
                }));

                console.log('Mapped Team List:', teamList); // Debugging log

                setTeams(teamList);

                // Set the first team as the default selected team
                if (teamList.length > 0) {
                    setSelectedTeam(teamList[0].TeamID);
                }
            } catch (err) {
                const errorMessage = 'Failed to load teams. Please try again later.';
                setError(errorMessage);
                if (onError) {
                    onError(errorMessage);
                }
                console.error('Error fetching teams:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchTeams();
    }, [competitionId, statsFeed]);

    // Fetch Player Stats
    useEffect(() => {
        const fetchPlayerStats = async () => {
            if (!selectedTeam) return;

            try {
                setLoading(true);
                const timestamp = new Date().getTime();
                const defaultFeed = `${feedURL}`;

                const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-${selectedTeam}-playerstats.js?callback=onplayerstats&_=${timestamp}`);
                const data = await response.text();

                const matches = data.match(/onplayerstats\((.*?)\);?$/s);
                if (!matches || !matches[1]) {
                    throw new Error('Invalid player stats data format');
                }

                const jsonStr = matches[1].trim();
                const parsedData = JSON.parse(jsonStr);

                if (!parsedData?.playerstats || !Array.isArray(parsedData.playerstats)) {
                    throw new Error('Invalid player stats structure');
                }

                setPlayerStats(parsedData.playerstats);
                // Set the first player as the default selected player
                if (parsedData.playerstats.length > 0) {
                    setSelectedPlayer(parsedData.playerstats[0]);
                }
            } catch (err) {
                const errorMessage = 'Failed to load player statistics. Please try again later.';
                setError(errorMessage);
                if (onError) onError(errorMessage);
                console.error('Error fetching player stats:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPlayerStats();
    }, [competitionId, selectedTeam, statsFeed]);

    const hasBattingStats = selectedPlayer && selectedPlayer.Innings > 0;
    const hasBowlingStats = selectedPlayer && selectedPlayer.InningsBowled > 0;
    useEffect(() => {
        if (selectedPlayer) {
            if (activeOption === 'batting' && !hasBattingStats) {
                if (hasBowlingStats) {
                    setActiveOption('bowling');
                }
            } else if (activeOption === 'bowling' && !hasBowlingStats) {
                if (hasBattingStats) {
                    setActiveOption('batting');
                }
            }
        }
    }, [selectedPlayer]);
    const handleTabClick = (tab) => {
        setActiveOption(tab);
    };

    const handlePlayerClick = (player) => {
        setSelectedPlayer(player);
    };
    const handleTeamChange = (event) => {
        const teamId = parseInt(event.target.value, 10);
        setSelectedTeam(teamId);
        setSelectedTeamId(teamId); // Update selectedTeamId for player stats
        setCurrentSlide(0);
    };
    // Update handlePrevClick to use Slick's methods
    const handlePrevClick = (e) => {
        e.preventDefault();
        if (sliderRef.current) {
            sliderRef.current.slickPrev();
        }
    };

    // Update handleNextClick to use Slick's methods
    const handleNextClick = (e) => {
        e.preventDefault();
        if (sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };
    const renderBattingStats = (player) => {
        if (!player || player.Innings === 0) return null;
        return (
            <ul className="statsList">
                <li><span>Matches</span><i>{player.Matches}</i></li>
                <li><span>Innings</span><i>{player.Innings}</i></li>
                <li><span>Runs</span><i>{player.RunsScored}</i></li>
                <li><span>Balls</span><i>{player.BallsFaced}</i></li>
                <li><span>SR</span><i>{player.StrikeRate}</i></li>
                <li><span>Average</span><i>{player.BattingAverage}</i></li>
                <li><span>Fours</span><i>{player.Bdry4Scored}</i></li>
                <li><span>Sixes</span><i>{player.Bdry6Scored}</i></li>
                <li><span>50's</span><i>{player.Fifties}</i></li>
                <li><span>100's</span><i>{player.Hundreds}</i></li>
                <li><span>BDRY FREQ.</span><i>{player.BdryFreq}</i></li>
                <li><span>BOUNDARY%</span><i>{player.BdryPercent}</i></li>
                <li><span>DOT BALLS</span><i>{player.DotBalls}</i></li>
                <li><span>DOT FREQ.</span><i>{player.DotBallFrequency}</i></li>
                <li><span>DOT BALL %</span><i>{player.DBPercent}</i></li>
            </ul>
        );
    };

    const renderBowlingStats = (player) => {
        if (!player || player.InningsBowled === 0) return null;
        return (
            <ul className="statsList">
                <li><span>Matches</span><i>{player.MatchesBowled}</i></li>
                <li><span>Innings</span><i>{player.InningsBowled}</i></li>
                <li><span>Overs</span><i>{player.OversBowled}</i></li>
                <li><span>Runs</span><i>{player.TotalRunsConceeded}</i></li>
                <li><span>Wickets</span><i>{player.WicketsTaken}</i></li>
                <li><span>Average</span><i>{player.BowlingAverage}</i></li>
                <li><span>Economy</span><i>{player.EconomyRate}</i></li>
                <li><span>SR</span><i>{player.BowlingSR}</i></li>
                <li><span>Maidens</span><i>{player.Maidens}</i></li>
                <li><span>Bdry Freq.</span><i>{player.BowlingBdryFrequency}</i></li>
                <li><span>Boundary %</span><i>{player.BowlingBdryPercentage}</i></li>
                <li><span>Dot Balls</span><i>{player.DotBallsBowled}</i></li>
                <li><span>Dot Freq.</span><i>{player.BowlingDotBallFrequency}</i></li>
                <li><span>Dot Ball %</span><i>{player.BowlingDotBallPercent}</i></li>
            </ul>
        );
    };

    // Conditional rendering function
    const renderContent = () => {
        if (loading) {
            return (
                <div className="loading-message">
                    Loading stats...
                </div>
            );
        }

        if (!playerStats || playerStats.length === 0) {
            return (
                <div className="no-stats-wrapper">
                    <div className="no-stats-message" style={{ color: 'white' }}>No stats found</div>
                </div>
            );
        }

        return (
            <>
                <div id="plyRotateSmipl" className="">
                    <div className="dataStreamSMIPL" style={{ maxWidth: '1280px' }}>
                        <div className="slider-viewport" aria-live="polite"
                            style={{ width: '100%', overflow: 'hidden', position: 'relative', height: '65px' }}>
                            <div className="mcPlyRotList"
                               
                            >
                                <Slider ref={sliderRef} {...sliderSettings}>
                                    {filteredPlayers.map((player, index) => (
                                        <div key={index}
                                            className="mcPlythumb"

                                            style={{ float: 'left', listStyle: 'none', position: 'relative' }}>
                                            <a
                                                data-playid={player.PlayerID}
                                                className={selectedPlayer?.PlayerID === player.PlayerID ? 'plyActive' : ''}
                                                onClick={() => handlePlayerClick(player)}
                                            >                                        <img
                                                    src={player.profileimg || defaultPlayerImage}
                                                    onError={handleImageError}
                                                    alt={player.PlayerName}
                                                />
                                                <span>
                                                    <i>{player.PlayerName}</i> <i>{player.Specialization}</i>
                                                </span>
                                            </a>
                                        </div>
                                    ))}
                                </Slider>
                            </div>
                        </div>
                        <div className="next-prev-controls NP-has-controls-direction">
                        <div className="next-prev-direction">
                                <a 
                                    className={`mcPrevArrow icon-angle-left ${currentSlide === 0 ? 'disabled' : ''}`}
                                    href=""
                                    onClick={(e) => currentSlide !== 0 && handlePrevClick(e)}
                                    style={{ pointerEvents: currentSlide === 0 ? 'none' : 'auto' }}
                                ></a>
                                <a 
                                    className={`mcNextArrow icon-angle-right ${isEndOfSlides ? 'disabled' : ''}`}
                                    href=""
                                    onClick={(e) => !isEndOfSlides && handleNextClick(e)}
                                    style={{ pointerEvents: isEndOfSlides ? 'none' : 'auto' }}
                                ></a>
                            </div>
                        </div>
                    </div>
                </div >

                <div className="col s12 m12 l5 pStatsWrap">
                    <div className="mcPlyStatsHeader">
                        <div className="plyImg">
                            <img className="mcPltPic"
                                src={selectedPlayer?.profileimg || defaultPlayerImage}
                                onError={handleImageError}
                                alt={selectedPlayer?.PlayerName || 'Player'} />
                        </div>
                        <div className="positionDetail">
                            <table className="player-details">
                                <tbody>
                                    <tr>
                                        <td>Role </td>
                                        <td className="">{selectedPlayer?.Specialization}</td>
                                    </tr>
                                    <tr>
                                        <td>Batting Style </td>
                                        <td className="">{selectedPlayer?.BattingType}</td>
                                    </tr>
                                    <tr>
                                        <td>Bowling Style </td>
                                        <td className="">{selectedPlayer?.BowlingType}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="mcPlyOption">
                                {hasBattingStats && (
                                    <span
                                        className={`batOption ${activeOption === "batting" ? "opActive" : ""}`}
                                        onClick={() => handleTabClick("batting")}>
                                        Batting
                                    </span>
                                )}
                                {hasBowlingStats && (
                                    <span
                                        className={`bowOption ${activeOption === "bowling" ? "opActive" : ""}`}
                                        onClick={() => handleTabClick("bowling")}>
                                        Bowling
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col s12 m12 l7 no-padding pStatsWrap">
                    {activeOption === 'batting'
                        ? renderBattingStats(selectedPlayer)
                        : renderBowlingStats(selectedPlayer)}
                </div>
            </>
        );
    }
    return (
        <div id="playerStatsWrapper">
            <div className="statsHeader">
                <div className="mcContainer">
                    <div className="mcSelectBox">
                        <div className="col s12 m6 l3">
                            <select
                                className="mcSelectDefault mcSearchTeam "
                                value={selectedTeam || ''}
                                onChange={handleTeamChange}
                            >
                                {teams.map(team => (
                                    <option key={team.TeamID} value={team.TeamID}>
                                        {team.TeamName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col s12 m6 l3">
                            <p className="mcSearchResults icon-search-1">
                                <input
                                    type="text"
                                    className="mcSearchDefault"
                                    placeholder="Type to search your player here..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                />
                            </p>
                        </div>
                    </div>

                    {renderContent()}

                </div>
            </div>
        </div>
    )
}
export default PlayerStats;